import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import OperatorLayout from "./OperatorLayout";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { Mail, Phone, RefreshCw, Trash2, ArrowLeft, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useOperatorApi } from "@/hooks/useOperatorApi";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface RequestRow {
  id: string;
  request_number: string;
  category: string;
  urgency: string;
  location: string | null;
  location_text: string | null;
  requester_email: string;
  requester_company: string | null;
  context: string | null;
  description: string | null;
  budget_range: string | null;
  is_matched: boolean;
  remote_allowed: boolean;
  onsite_required: boolean;
  compliance_relevant: boolean;
  decision_authority: string;
  project_size: string | null;
  project_systems_range: string | null;
  project_users_range: string | null;
  calculated_price: number | null;
  created_at: string;
  contact_data: Record<string, any> | null;
}

export default function OperatorRequests() {
  const { t } = useTranslation();
  const { operatorFetch } = useOperatorApi();

  const categoryLabels: Record<string, string> = {
    support: t("op.catSupport"),
    managed_service: t("op.catManaged"),
    project: t("op.catProject"),
    security: t("op.catSecurity"),
    cloud: t("op.catCloud"),
  };

  const urgencyLabels: Record<string, string> = {
    immediate: t("op.urgImmediate"),
    within_48h: t("op.urgWithin48h"),
    within_7_days: t("op.urgWithin7d"),
    planned: t("op.urgPlanned"),
  };

  const decisionLabels: Record<string, string> = {
    self: t("op.decSelf"),
    team: t("op.decTeam"),
    recommend: t("op.decRecommend"),
  };
  const [requests, setRequests] = useState<RequestRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<"all" | "matched" | "unmatched">("all");
  const [matching, setMatching] = useState<string | null>(null);

  // Detail view
  const [detailId, setDetailId] = useState<string | null>(null);
  const [detail, setDetail] = useState<any>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Delete
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; number: string } | null>(null);
  const [deleting, setDeleting] = useState(false);

  const loadRequests = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter === "all" ? "" : `?status=${filter}`;
      const data = await operatorFetch(`requests${params}`);
      setRequests(data || []);
    } catch {
      toast.error(t("op.loadError"));
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, [operatorFetch, filter]);

  useEffect(() => { loadRequests(); }, [loadRequests]);

  const loadDetail = async (id: string) => {
    setDetailId(id);
    setDetailLoading(true);
    try {
      const data = await operatorFetch(`requests/${id}`);
      setDetail(data);
    } catch {
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const triggerMatch = async (requestId: string) => {
    setMatching(requestId);
    try {
      await operatorFetch(`requests/${requestId}/rematch`, { method: "POST" });
      toast.success(t("op.matchingStarted"));
      loadRequests();
      if (detailId === requestId) loadDetail(requestId);
    } catch (err: any) {
      toast.error(err.message || t("op.matchingFailed"));
    } finally {
      setMatching(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await operatorFetch(`requests/${deleteTarget.id}`, { method: "DELETE" });
      toast.success(t("op.requestDeleted", { number: deleteTarget.number }));
      setDeleteOpen(false);
      setDeleteTarget(null);
      if (detailId === deleteTarget.id) { setDetailId(null); setDetail(null); }
      loadRequests();
    } catch (err: any) {
      toast.error(err.message || t("op.deleteError"));
    } finally {
      setDeleting(false);
    }
  };

  const phoneFromContext = (ctx: string | null) => {
    if (!ctx) return null;
    const match = ctx.match(/Telefon:\s*(.+)/);
    return match?.[1]?.trim() || null;
  };

  // Detail view
  if (detailId) {
    return (
      <OperatorLayout title={t("op.reqDetailTitle")} subtitle={detail?.request_number || t("common.loading")}>
        <Button variant="quiet" size="sm" onClick={() => { setDetailId(null); setDetail(null); }} className="gap-1.5 mb-4">
          <ArrowLeft className="w-4 h-4" /> {t("op.backToList")}
        </Button>

        {detailLoading ? (
          <div className="p-12 text-center text-caption">{t("common.loading")}</div>
        ) : detail ? (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Main info */}
            <div className="lg:col-span-8 space-y-6">
              <div className="border border-border bg-card p-6 space-y-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h2 className="text-subheadline">{detail.request_number}</h2>
                    <p className="text-caption">
                      {new Date(detail.created_at).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={cn(
                      "text-xs px-2 py-0.5 border",
                      detail.is_matched ? "border-accent/30 text-accent" : "border-border text-muted-foreground"
                    )}>
                      {detail.is_matched ? t("op.matched") : t("op.open")}
                    </span>
                    <Button
                      variant="quiet"
                      size="icon"
                      className="h-8 w-8 text-destructive hover:text-destructive"
                      onClick={() => { setDeleteTarget({ id: detail.id, number: detail.request_number }); setDeleteOpen(true); }}
                      title={t("op.deleteRequest")}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                  <div><span className="text-micro block">{t("op.labelCategory")}</span>{categoryLabels[detail.category] || detail.category}</div>
                  <div><span className="text-micro block">{t("op.labelUrgency")}</span>{urgencyLabels[detail.urgency] || detail.urgency}</div>
                  <div><span className="text-micro block">{t("op.labelRegion")}</span>{detail.location_text || detail.location || "—"}</div>
                  <div><span className="text-micro block">{t("op.labelRemote")}</span>{detail.remote_allowed ? t("common.yes") : t("common.no")}</div>
                  <div><span className="text-micro block">{t("op.labelOnsite")}</span>{detail.onsite_required ? t("common.yes") : t("common.no")}</div>
                  <div><span className="text-micro block">{t("op.labelCompliance")}</span>{detail.compliance_relevant ? t("common.yes") : t("common.no")}</div>
                  <div><span className="text-micro block">{t("op.labelDecision")}</span>{decisionLabels[detail.decision_authority] || detail.decision_authority}</div>
                  <div><span className="text-micro block">{t("op.labelBudget")}</span>{detail.budget_range || "—"}</div>
                  <div><span className="text-micro block">{t("op.labelPrice")}</span>{detail.calculated_price ? `${detail.calculated_price} €` : "—"}</div>
                  {detail.project_size && <div><span className="text-micro block">{t("op.labelProjectSize")}</span>{detail.project_size}</div>}
                  {detail.project_systems_range && <div><span className="text-micro block">{t("op.labelSystems")}</span>{detail.project_systems_range}</div>}
                  {detail.project_users_range && <div><span className="text-micro block">{t("op.labelUsers")}</span>{detail.project_users_range}</div>}
                </div>
              </div>

              {/* Description / Context */}
              {(detail.description || detail.context) && (
                <div className="border border-border bg-card p-6 space-y-3">
                  {detail.description && (
                    <div>
                      <span className="text-micro block mb-1">{t("op.labelDescription")}</span>
                      <p className="text-sm whitespace-pre-wrap">{detail.description}</p>
                    </div>
                  )}
                  {detail.context && (
                    <div>
                      <span className="text-micro block mb-1">{t("op.labelContext")}</span>
                      <p className="text-caption whitespace-pre-wrap">{detail.context}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Stack Tags */}
              {detail.stacks?.length > 0 && (
                <div className="border border-border bg-card p-6">
                  <span className="text-micro block mb-2">{t("op.labelStackTags")}</span>
                  <div className="flex flex-wrap gap-1.5">
                    {detail.stacks.map((s: any) => (
                      <span key={s.id} className="text-xs px-2 py-0.5 border border-border bg-secondary/30">
                        {s.stack_tags?.name || s.stack_tag_id}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Opportunities */}
              {detail.opportunities?.length > 0 && (
                <div className="border border-border bg-card p-6 space-y-3">
                  <span className="text-micro block">{t("op.labelOpportunities")} ({detail.opportunities.length})</span>
                  <div className="divide-y divide-border">
                    {detail.opportunities.map((opp: any) => (
                      <div key={opp.id} className="py-3 flex items-center justify-between text-sm">
                        <div>
                          <p className="font-medium">{opp.opportunity_number}</p>
                          <p className="text-caption">{opp.provider_profiles?.company_name || "—"} · {opp.provider_profiles?.region || "—"}</p>
                        </div>
                        <div className="text-right">
                          <span className={cn(
                            "text-xs px-2 py-0.5 border",
                            opp.status === "accepted" ? "border-accent/30 text-accent" :
                            opp.status === "declined" ? "border-destructive/30 text-destructive" :
                            "border-border text-muted-foreground"
                          )}>
                            {opp.status}
                          </span>
                          <p className="text-caption mt-1">{opp.price} € · Score {Math.round(opp.match_score * 100)}%</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Match Runs */}
              {detail.match_runs?.length > 0 && (
                <div className="border border-border bg-card p-6 space-y-3">
                  <span className="text-micro block">{t("op.labelMatchRuns")} ({detail.match_runs.length})</span>
                  <div className="divide-y divide-border">
                    {detail.match_runs.map((run: any) => (
                      <div key={run.id} className="py-3 text-sm flex items-center justify-between">
                        <div>
                          <p className="text-caption">
                            {new Date(run.created_at).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                          </p>
                          <p className="text-micro">v{run.algorithm_version}</p>
                        </div>
                        <p className="text-sm">{run.selected_count} / {run.candidate_count} {t("op.selected")}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-4 space-y-4">
              <div className="border border-border bg-card p-6 space-y-3">
                <span className="text-micro block">{t("op.labelRequester")}</span>
                {detail.contact_data?.company ? (
                  <div className="space-y-2">
                    <p className="text-sm font-medium">{detail.contact_data.company.name || detail.requester_company || "—"}</p>
                    {detail.contact_data.company.street && <p className="text-xs text-muted-foreground">{detail.contact_data.company.street}</p>}
                    {(detail.contact_data.company.postal_code || detail.contact_data.company.city) && (
                      <p className="text-xs text-muted-foreground">{[detail.contact_data.company.postal_code, detail.contact_data.company.city].filter(Boolean).join(" ")}</p>
                    )}
                    {detail.contact_data.company.phone && (
                      <div className="flex items-center gap-2 mt-1">
                        <Phone className="w-3.5 h-3.5 text-muted-foreground" />
                        <p className="text-sm">{detail.contact_data.company.phone}</p>
                      </div>
                    )}
                    {detail.contact_data.company.email && (
                      <div className="flex items-center gap-2 mt-1">
                        <Mail className="w-3.5 h-3.5 text-muted-foreground" />
                        <p className="text-sm">{detail.contact_data.company.email}</p>
                      </div>
                    )}
                    {detail.contact_data.company.note && (
                      <p className="text-xs text-muted-foreground mt-2 italic">{detail.contact_data.company.note}</p>
                    )}
                    {detail.contact_data.contacts?.map((c: any, i: number) => (
                      <div key={i} className="mt-3 pt-3 border-t border-border space-y-1">
                        <p className="text-xs text-muted-foreground uppercase">{i === 0 ? t("inquiry.contactPersonTitle", "Ansprechpartner") : t("inquiry.additionalContact", "Weiterer Ansprechpartner")}</p>
                        <p className="text-sm font-medium">{c.name}{c.position ? ` · ${c.position}` : ""}</p>
                        {c.email && <div className="flex items-center gap-2"><Mail className="w-3 h-3 text-muted-foreground" /><p className="text-sm">{c.email}</p></div>}
                        {c.phone && <div className="flex items-center gap-2"><Phone className="w-3 h-3 text-muted-foreground" /><p className="text-sm">{c.phone}</p></div>}
                        {c.mobile && <div className="flex items-center gap-2"><Phone className="w-3 h-3 text-muted-foreground" /><p className="text-sm">{c.mobile}</p></div>}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div>
                    <p className="text-sm font-medium">{detail.requester_company || "—"}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <Mail className="w-3.5 h-3.5 text-muted-foreground" />
                      <p className="text-sm">{detail.requester_email}</p>
                    </div>
                    {phoneFromContext(detail.context) && (
                      <div className="flex items-center gap-2 mt-1">
                        <Phone className="w-3.5 h-3.5 text-muted-foreground" />
                        <p className="text-sm">{phoneFromContext(detail.context)}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Risk flags */}
              {detail.request_risk_flags?.length > 0 && (
                <div className="border border-border bg-card p-6 space-y-2">
                  <span className="text-micro block">{t("op.labelRiskFlags")}</span>
                  {detail.request_risk_flags.map((flag: any) => (
                    <div key={flag.id} className="text-sm p-3 border border-border bg-secondary/10">
                      <p className="font-medium">{flag.status} (Score: {flag.score})</p>
                      {flag.operator_note && <p className="text-caption mt-1">{flag.operator_note}</p>}
                    </div>
                  ))}
                </div>
              )}

              {/* Actions */}
              <div className="border border-border bg-card p-6 space-y-3">
                {!detail.is_matched && (
                  <Button
                    variant="commit"
                    size="sm"
                    className="w-full"
                    disabled={matching === detail.id}
                    onClick={() => triggerMatch(detail.id)}
                  >
                    {matching === detail.id ? t("op.matchingRunning") : t("op.startMatching")}
                  </Button>
                )}
                <Button
                  variant="quiet"
                  size="sm"
                  className="w-full gap-1.5 text-destructive hover:text-destructive"
                  onClick={() => { setDeleteTarget({ id: detail.id, number: detail.request_number }); setDeleteOpen(true); }}
                >
                  <Trash2 className="w-4 h-4" /> {t("op.deleteRequest")}
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="p-12 text-center text-caption">{t("op.requestNotFound")}</div>
        )}

        {/* Delete dialog (also rendered here for detail view) */}
        <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t("op.deleteRequestTitle")}</AlertDialogTitle>
              <AlertDialogDescription>
                <span dangerouslySetInnerHTML={{ __html: t("op.deleteRequestDesc", { number: deleteTarget?.number }) }} />
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t("op.cancelAction")}</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} disabled={deleting} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                {deleting ? t("op.deleting") : t("op.deleteForever")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </OperatorLayout>
    );
  }

  // List view
  return (
    <OperatorLayout title={t("op.reqTitle")} subtitle={t("op.reqSubtitle")}>
      <div className="space-y-4">
        {/* Filters */}
        <div className="flex gap-2 flex-wrap">
          {(["all", "unmatched", "matched"] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={cn(
                "px-3 py-1.5 text-xs border transition-colors",
                filter === f
                  ? "border-accent bg-accent/5 text-foreground"
                  : "border-border text-muted-foreground hover:text-foreground"
              )}
            >
              {f === "all" ? t("op.filterAll") : f === "unmatched" ? t("op.filterUnmatched") : t("op.filterMatched")}
            </button>
          ))}
          <button
            onClick={loadRequests}
            className="px-3 py-1.5 text-xs border border-border text-muted-foreground hover:text-foreground transition-colors ml-auto"
          >
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Table */}
        <div className="border border-border bg-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left p-3 text-micro font-normal">{t("op.colNumber")}</th>
                <th className="text-left p-3 text-micro font-normal">{t("op.colCategory")}</th>
                <th className="text-left p-3 text-micro font-normal">{t("op.colRegion")}</th>
                <th className="text-left p-3 text-micro font-normal">{t("op.colCompany")}</th>
                <th className="text-left p-3 text-micro font-normal">{t("op.colUrgency")}</th>
                <th className="text-left p-3 text-micro font-normal">{t("op.colStatus")}</th>
                <th className="text-left p-3 text-micro font-normal">{t("op.colPrice")}</th>
                <th className="text-left p-3 text-micro font-normal">{t("op.colDate")}</th>
                <th className="text-left p-3 text-micro font-normal"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={9} className="p-8 text-center text-caption">{t("common.loading")}</td>
                </tr>
              ) : requests.length === 0 ? (
                <tr>
                  <td colSpan={9} className="p-8 text-center text-caption">{t("op.noRequests")}</td>
                </tr>
              ) : (
                requests.map((req) => (
                  <tr
                    key={req.id}
                    className="border-b border-border cursor-pointer hover:bg-secondary/30 transition-colors"
                    onClick={() => loadDetail(req.id)}
                  >
                    <td className="p-3 font-medium">{req.request_number}</td>
                    <td className="p-3 text-caption">{categoryLabels[req.category] || req.category}</td>
                    <td className="p-3 text-caption">{req.location || "—"}</td>
                    <td className="p-3 text-caption">{req.requester_company || "—"}</td>
                    <td className="p-3">
                      <span className="text-xs px-2 py-0.5 border border-border">
                        {urgencyLabels[req.urgency] || req.urgency}
                      </span>
                    </td>
                    <td className="p-3">
                      <span className={cn(
                        "text-xs px-2 py-0.5 border",
                        req.is_matched ? "border-accent/30 text-accent" : "border-border text-muted-foreground"
                      )}>
                        {req.is_matched ? t("op.matched") : t("op.open")}
                      </span>
                    </td>
                    <td className="p-3 price-display">{req.calculated_price ?? "—"} €</td>
                    <td className="p-3 text-caption">
                      {new Date(req.created_at).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                    </td>
                    <td className="p-3">
                      <Button
                        variant="quiet"
                        size="icon"
                        className="h-7 w-7 text-destructive hover:text-destructive"
                        onClick={(e) => { e.stopPropagation(); setDeleteTarget({ id: req.id, number: req.request_number }); setDeleteOpen(true); }}
                      title={t("op.deleteRequest")}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Delete dialog */}
      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("op.deleteRequestTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              <span dangerouslySetInnerHTML={{ __html: t("op.deleteRequestDesc", { number: deleteTarget?.number }) }} />
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("op.cancelAction")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={deleting} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {deleting ? t("op.deleting") : t("op.deleteForever")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </OperatorLayout>
  );
}
